import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSubscription } from '../contexts/SubscriptionContext';

/**
 * Route guard for direct URL access to premium pages. If the user is
 * subscribed (or admin), renders the page. If not authenticated, sends
 * them to /login. If authed-but-not-subscribed, sends them home and
 * opens the global subscription-gate modal so the popup appears over
 * the library rather than as a standalone page.
 *
 * Post-checkout grace: if the URL carries `?subscription=success` the
 * SubscriptionReturnHandler in App.js is polling /api/billing/status
 * waiting for the Stripe webhook. Don't bounce the user home in that
 * window — render a small loading state instead so they stay on the
 * book they were trying to open.
 */
export default function PremiumRoute({ children }) {
  const sub = useSubscription();
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const isPostCheckout = params.get('subscription') === 'success';

  useEffect(() => {
    if (sub.loading) return;
    const token = localStorage.getItem('adeeb-token');
    if (!token) {
      navigate(`/login?next=${encodeURIComponent(location.pathname + location.search)}`, { replace: true });
      return;
    }
    if (!sub.active && !isPostCheckout) {
      sub.openGate(location.pathname + location.search);
      navigate('/', { replace: true });
    }
  }, [sub, location.pathname, location.search, navigate, isPostCheckout]);

  if (sub.loading) return null;

  if (!sub.active && isPostCheckout) {
    return (
      <div style={{
        minHeight: '60vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 14,
        color: 'var(--ink, #2a1f14)',
        textAlign: 'center',
        padding: 24,
      }}>
        <div className="loading-spinner" />
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '1.2rem' }}>
          Activating your subscription…
        </div>
        <div style={{ fontFamily: "'Inter', system-ui, sans-serif", fontSize: '0.85rem', opacity: 0.65 }}>
          This usually takes a few seconds. Don't close the tab.
        </div>
      </div>
    );
  }

  if (!sub.active) return null;
  return children;
}
